/**
 * Pure utility functions for Bulk Customer Info Entry in EInvoiceCreateDraftModal.
 * Extracted for testability — no Vue/Vuex dependencies.
 */
import {
  BUYER_VALIDATION,
  TAX_CODE_FORMAT_ERROR_MESSAGE,
  isValidEmailList,
  normalizeEmailList,
} from '@/utils/einvoice.enum'

/**
 * Detect whether batch bills likely belong to the same customer.
 * Returns 'SHARED' or 'PER_BILL'.
 *
 * Note: Mode names use 'PER_BILL' instead of 'INDIVIDUAL' to avoid
 * confusion with buyer type 'INDIVIDUAL' (Cá nhân).
 *
 * @param {Array<{ billId, customerId?, phone? }>} bills
 * @returns {'SHARED'|'PER_BILL'}
 */
function detectDefaultMode(bills) {
  if (!bills || bills.length <= 1) return 'SHARED'

  // Try customerId first
  const customerIds = [...new Set(bills.map((b) => b.customerId).filter(Boolean))]
  if (customerIds.length === 1) return 'SHARED'

  // Fallback to phone
  const phones = [...new Set(bills.map((b) => b.phone).filter(Boolean))]
  if (phones.length === 1) return 'SHARED'

  // If no customer data at all, or mixed → PER_BILL (each bill gets its own buyer info)
  return 'PER_BILL'
}

// ─── Field Merge Logic ──────────────────────────────────────────────────────

const FIELD_ACTION = Object.freeze({
  KEEP: 'KEEP',
  OVERRIDE: 'OVERRIDE',
  CLEAR: 'CLEAR',
})

/**
 * Analyze field values across bills to detect mixed/uniform state.
 *
 * @param {Array<Object>} bills - array of bill buyer data objects
 * @param {string[]} fieldNames - fields to analyze
 * @returns {Record<string, { isMixed: boolean, uniformValue: string|null, distinctValues: string[] }>}
 */
function computeFieldStates(bills, fieldNames) {
  const result = {}
  for (const field of fieldNames) {
    const values = bills.map((b) => (b[field] ?? '').toString())
    const distinct = [...new Set(values)]
    result[field] = {
      isMixed: distinct.length > 1,
      uniformValue: distinct.length === 1 ? distinct[0] : null,
      distinctValues: distinct.length > 1 ? distinct : [],
    }
  }
  return result
}

/**
 * Apply field-level overrides to a list of bills.
 * Returns a NEW array (does not mutate input).
 *
 * @param {Array<Object>} bills
 * @param {Record<string, { action: 'KEEP'|'OVERRIDE'|'CLEAR', value?: string }>} overrides
 * @returns {Array<Object>}
 */
function applyFieldOverrides(bills, overrides) {
  return bills.map((bill) => {
    const merged = { ...bill }
    for (const [field, override] of Object.entries(overrides)) {
      if (override.action === FIELD_ACTION.OVERRIDE) {
        merged[field] = override.value ?? ''
      } else if (override.action === FIELD_ACTION.CLEAR) {
        merged[field] = ''
      }
      // KEEP → do nothing, original value preserved
    }
    return merged
  })
}

// ─── Grid Row Validation ────────────────────────────────────────────────────

const ROW_STATUS = Object.freeze({
  VALID: 'VALID',
  MISSING: 'MISSING',
  INVALID: 'INVALID',
})

const BUYER_TYPE = Object.freeze({
  INDIVIDUAL: 'INDIVIDUAL',
  ENTERPRISE: 'ENTERPRISE',
})

const RETAIL_BUYER_GRID_FIELDS = Object.freeze([
  'buyerName',
  'buyerTaxCode',
  'buyerAddress',
  'buyerEmail',
  'buyerPhone',
  'buyerIdNumber',
])

function normalizeDraftField(value) {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value).trim()
}

function createRetailBuyerDraftBucket(source = {}) {
  return {
    buyerName: normalizeDraftField(source.buyerName),
    buyerTaxCode: normalizeDraftField(source.buyerTaxCode),
    buyerAddress: normalizeDraftField(source.buyerAddress),
    buyerEmail: normalizeDraftField(source.buyerEmail),
    buyerPhone: normalizeDraftField(source.buyerPhone),
    buyerIdNumber: normalizeDraftField(source.buyerIdNumber),
  }
}

function getRetailDraftKey(buyerType) {
  return buyerType === BUYER_TYPE.INDIVIDUAL ? 'buyerIndividualDraft' : 'buyerCompanyDraft'
}

function isRetailFacetBackedRow(row = {}) {
  return Boolean(row.facetBuyerCompany || row.facetBuyerIndividual)
}

function applyDraftBucketToRow(row, bucket) {
  const nextRow = { ...row }

  RETAIL_BUYER_GRID_FIELDS.forEach((field) => {
    nextRow[field] = normalizeDraftField(bucket[field])
  })

  return nextRow
}

function syncRetailRowDraftFromActiveFields(row = {}) {
  if (!isRetailFacetBackedRow(row)) {
    return { ...row }
  }

  const draftKey = getRetailDraftKey(row.buyerType)
  const nextDraft = createRetailBuyerDraftBucket(row[draftKey] || {})

  RETAIL_BUYER_GRID_FIELDS.forEach((field) => {
    nextDraft[field] = normalizeDraftField(row[field])
  })

  return {
    ...row,
    [draftKey]: nextDraft,
    _dirty: true,
  }
}

function switchRetailRowBuyerType(row = {}, nextBuyerType = BUYER_TYPE.ENTERPRISE) {
  if (!isRetailFacetBackedRow(row)) {
    return {
      ...row,
      buyerType: nextBuyerType,
    }
  }

  const syncedRow = syncRetailRowDraftFromActiveFields(row)
  const nextDraftKey = getRetailDraftKey(nextBuyerType)
  const nextDraft = createRetailBuyerDraftBucket(syncedRow[nextDraftKey] || {})

  return applyDraftBucketToRow(
    {
      ...syncedRow,
      buyerType: nextBuyerType,
    },
    nextDraft,
  )
}

function applyOverridesToRetailRow(row, overrides = {}) {
  const merged = { ...row }

  Object.entries(overrides || {}).forEach(([field, override]) => {
    if (!RETAIL_BUYER_GRID_FIELDS.includes(field)) {
      return
    }

    if (override.action === FIELD_ACTION.OVERRIDE) {
      merged[field] = override.value ?? ''
    } else if (override.action === FIELD_ACTION.CLEAR) {
      merged[field] = ''
    }
  })

  return merged
}

function materializeSharedOverridesToRows(
  rows = [],
  { buyerType = BUYER_TYPE.ENTERPRISE, overrides = {}, preserveDirty = true } = {},
) {
  return (rows || []).map((row) => {
    if (!isRetailFacetBackedRow(row)) {
      return preserveDirty && row._dirty ? { ...row } : applyFieldOverrides([row], overrides)[0]
    }

    if (preserveDirty && row._dirty) {
      return { ...row }
    }

    const switchedRow = switchRetailRowBuyerType(
      {
        ...row,
        _dirty: false,
      },
      buyerType,
    )
    const mergedRow = applyOverridesToRetailRow(switchedRow, overrides)

    return {
      ...mergedRow,
      ...syncRetailRowDraftFromActiveFields({
        ...mergedRow,
        _dirty: false,
      }),
      _dirty: false,
    }
  })
}

function resolveDraftPayloadField(row = {}, buyerField, customerField) {
  const value = Object.prototype.hasOwnProperty.call(row, buyerField)
    ? row[buyerField]
    : row[customerField]
  return value ?? ''
}

function normalizeDraftRowForPayload(row = {}) {
  const syncedRow = isRetailFacetBackedRow(row) ? syncRetailRowDraftFromActiveFields(row) : row

  // contactPersonName chỉ có giá trị khi row được set explicit (ENTERPRISE có ô "Người mua hàng").
  // INDIVIDUAL không có ô đó → mặc định rỗng. KHÔNG fallback customerName.
  return {
    ...syncedRow,
    customerName: resolveDraftPayloadField(syncedRow, 'buyerName', 'customerName'),
    customerEmail: normalizeEmailList(
      resolveDraftPayloadField(syncedRow, 'buyerEmail', 'customerEmail'),
    ),
    customerPhone: resolveDraftPayloadField(syncedRow, 'buyerPhone', 'customerPhone'),
    customerTaxCode: resolveDraftPayloadField(syncedRow, 'buyerTaxCode', 'customerTaxCode'),
    customerAddress: resolveDraftPayloadField(syncedRow, 'buyerAddress', 'customerAddress'),
    customerIdNumber: resolveDraftPayloadField(syncedRow, 'buyerIdNumber', 'customerIdNumber'),
    contactPersonName: syncedRow.contactPersonName || '',
  }
}

/**
 * Validate a single grid row's buyer data.
 * Returns { status: ROW_STATUS, errors: Record<string, string> }.
 *
 * Priority: INVALID (format errors) > MISSING (required empty) > VALID
 *
 * Supports 2 active buyer types in current draft flow:
 * - INDIVIDUAL: Cá nhân (requires name only)
 * - ENTERPRISE: Doanh nghiệp (requires name, tax code, address, email)
 *
 * @param {Object} row
 * @returns {{ status: string, errors: Record<string, string> }}
 */
function validateGridRow(row) {
  const errors = {}
  let hasMissing = false
  let hasInvalid = false

  // INDIVIDUAL: Cá nhân Việt Nam
  if (row.buyerType === BUYER_TYPE.INDIVIDUAL) {
    if (!row.buyerName?.trim()) {
      errors.buyerName = 'Thiếu tên người mua'
      hasMissing = true
    }
    // Email optional for individual, but validate format if provided
    // Cho phép nhiều email cách nhau dấu phẩy — kiểm tra từng email
    if (row.buyerEmail?.trim() && !isValidEmailList(row.buyerEmail)) {
      errors.buyerEmail = 'Email sai định dạng'
      hasInvalid = true
    }
    if (row.buyerTaxCode?.trim() && !BUYER_VALIDATION.TAX_CODE.test(row.buyerTaxCode.trim())) {
      errors.buyerTaxCode = TAX_CODE_FORMAT_ERROR_MESSAGE
      hasInvalid = true
    }
  }

  // ENTERPRISE: Doanh nghiệp/Tổ chức
  if (row.buyerType === BUYER_TYPE.ENTERPRISE || !row.buyerType) {
    if (!row.buyerTaxCode?.trim()) {
      errors.buyerTaxCode = 'Thiếu MST'
      hasMissing = true
    } else if (!BUYER_VALIDATION.TAX_CODE.test(row.buyerTaxCode.trim())) {
      errors.buyerTaxCode = TAX_CODE_FORMAT_ERROR_MESSAGE
      hasInvalid = true
    }
    if (!row.buyerName?.trim()) {
      errors.buyerName = 'Thiếu tên doanh nghiệp'
      hasMissing = true
    }
    if (!row.buyerAddress?.trim()) {
      errors.buyerAddress = 'Thiếu địa chỉ'
      hasMissing = true
    }
    if (!row.buyerEmail?.trim()) {
      errors.buyerEmail = 'Thiếu email'
      hasMissing = true
    } else if (!isValidEmailList(row.buyerEmail)) {
      // Cho phép nhiều email cách nhau dấu phẩy — kiểm tra từng email
      errors.buyerEmail = 'Email sai định dạng'
      hasInvalid = true
    }
  }
  const status = hasInvalid
    ? ROW_STATUS.INVALID
    : hasMissing
      ? ROW_STATUS.MISSING
      : ROW_STATUS.VALID

  return { status, errors }
}

/**
 * Validate all rows in the grid. Returns per-row results + summary counts.
 *
 * @param {Array<Object>} rows
 * @returns {{ rows: Array<{ billId, status, errors }>, summary: { valid, missing, invalid } }}
 */
function validateGrid(rows) {
  const results = (rows || []).map((row) => {
    const validation = validateGridRow(row)
    return { billId: row.billId, ...validation }
  })

  const summary = {
    valid: results.filter((r) => r.status === ROW_STATUS.VALID).length,
    missing: results.filter((r) => r.status === ROW_STATUS.MISSING).length,
    invalid: results.filter((r) => r.status === ROW_STATUS.INVALID).length,
  }

  return { rows: results, summary }
}

// ─── Batch Size Warning ─────────────────────────────────────────────────────

const DEFAULT_BATCH_WARN_THRESHOLD = 30

/**
 * Returns true if the batch is large enough to warrant a performance warning.
 * @param {number} count
 * @param {number} [threshold=30]
 * @returns {boolean}
 */
function shouldWarnBatchSize(count, threshold = DEFAULT_BATCH_WARN_THRESHOLD) {
  return count > threshold
}

export {
  detectDefaultMode,
  computeFieldStates,
  applyFieldOverrides,
  FIELD_ACTION,
  validateGridRow,
  validateGrid,
  ROW_STATUS,
  BUYER_TYPE,
  RETAIL_BUYER_GRID_FIELDS,
  createRetailBuyerDraftBucket,
  syncRetailRowDraftFromActiveFields,
  switchRetailRowBuyerType,
  materializeSharedOverridesToRows,
  normalizeDraftRowForPayload,
  isRetailFacetBackedRow,
  shouldWarnBatchSize,
  DEFAULT_BATCH_WARN_THRESHOLD,
}
